import { Component } from 'opus-angular-seed-core';

@Component({
    bindings: {
        company: '='
    },
    template: `
        <div class="heading01 type01">
            <h1>{{ 'general.Privacy & data' | translate }}</h1>
        </div>
        <form class="well type02 mt2em form settings-privacy" ng-submit="vm.confirmAndSave()">
            <form-wrapper form="vm.form">
                <div class="row">
                    <label class="label">{{ 'company.Anonymize inactive users' | translate }}</label>
                    <div class="items">
                        <label class="switch01">
                            <input
                                name="isInactiveUserAnonymizationEnabled"
                                type="checkbox"
                                ng-model="vm.form.model.isInactiveUserAnonymizationEnabled"
                                ng-change="vm.loadAffectedUserCount()">
                            <span></span>
                        </label>
                    </div>
                </div>
                <div class="alert alert-warn has-icon">
                    <img class="alert-icon" alt="" src="assets/css/icon_warn.svg">
                    {{ 'company.inactiveUserAnonymization.description' | translate }}
                </div>
                <form-row
                    additional-classes="anonymization-period"
                    error="vm.form.errors.inactiveUserAnonymizationPeriod
                        || vm.form.errors.inactiveUserAnonymizationPeriodType"
                    ng-if="vm.form.model.isInactiveUserAnonymizationEnabled">
                    <label class="label">{{ 'company.Inactivity period' | translate }}</label>
                    <div class="items">
                        <input
                            type="number"
                            step="1"
                            class="form-control small02"
                            ng-model="vm.form.model.inactiveUserAnonymizationPeriod"
                            ng-change="vm.scheduleAffectedUserCount()">
                        <select
                            class="form-control small02"
                            ng-model="vm.form.model.inactiveUserAnonymizationPeriodType"
                            ng-change="vm.loadAffectedUserCount()">
                            <option value="{{ vm.PERIOD_TYPE_ENUM.KEYS.DAY }}">
                                {{ vm.PERIOD_TYPE_ENUM.LABELS.DAY }}
                            </option>
                            <option value="{{ vm.PERIOD_TYPE_ENUM.KEYS.WEEK }}">
                                {{ vm.PERIOD_TYPE_ENUM.LABELS.WEEK }}
                            </option>
                            <option value="{{ vm.PERIOD_TYPE_ENUM.KEYS.MONTH }}">
                                {{ vm.PERIOD_TYPE_ENUM.LABELS.MONTH }}
                            </option>
                        </select>
                        <span class="affected-user-count" ng-if="vm.affectedUserCount !== null">
                            {{ 'company.inactiveUserAnonymization.affectedUserCount'
                                | translate:{ count: vm.affectedUserCount } }}
                        </span>
                        <form-row-error></form-row-error>
                    </div>
                </form-row>
                <div class="alert alert-plain" ng-if="!vm.form.model.isInactiveUserAnonymizationEnabled">
                    {{ 'company.Inactive users are not anonymized automatically.' | translate }}
                </div>
            </form-wrapper>
        </form>
        <ul class="actions01">
            <li>
                <a ng-click="vm.confirmAndSave()" class="btn main">{{ 'general.Save' | translate }}</a>
            </li>
        </ul>
    `
})

export default class {
    constructor ($translate, lodash, FormFactory, toastr, CompanyResource, ModalService, $timeout,
        OCCURRENCE_ACCESS_PERIOD_TYPE_ENUM) {
        this.$translate = $translate;
        this.lodash = lodash;
        this.FormFactory = FormFactory;
        this.toastr = toastr;
        this.CompanyResource = CompanyResource;
        this.ModalService = ModalService;
        this.$timeout = $timeout;
        this.affectedUserCountTimer = null;
        this.PERIOD_TYPE_ENUM = OCCURRENCE_ACCESS_PERIOD_TYPE_ENUM;
        this.affectedUserCount = null;
        this.initForm();
        this.loadAffectedUserCount();
    }

    initForm () {
        this.form = this.FormFactory.create(
            this.company,
            model => model.$update({ id: model.id, expand: 'languages' })
        );
        const model = this.form.model;
        model.isInactiveUserAnonymizationEnabled = Boolean(model.isInactiveUserAnonymizationEnabled);
        if (!model.inactiveUserAnonymizationPeriodType) {
            model.inactiveUserAnonymizationPeriodType = this.PERIOD_TYPE_ENUM.KEYS.MONTH;
        }
    }

    $onDestroy () {
        this.$timeout.cancel(this.affectedUserCountTimer);
    }

    // the model itself is not debounced, so Save always sends what is in the field; only the count request waits
    scheduleAffectedUserCount () {
        this.$timeout.cancel(this.affectedUserCountTimer);
        this.affectedUserCountTimer = this.$timeout(() => this.loadAffectedUserCount(), 400);
    }

    loadAffectedUserCount () {
        this.$timeout.cancel(this.affectedUserCountTimer);
        const model = this.form.model;
        if (!model.isInactiveUserAnonymizationEnabled || !(model.inactiveUserAnonymizationPeriod >= 1)) {
            this.affectedUserCountRequest = null;
            this.affectedUserCount = null;
            return;
        }

        const request = this.CompanyResource.inactiveUserAnonymizationPreview({
            period: model.inactiveUserAnonymizationPeriod,
            periodType: model.inactiveUserAnonymizationPeriodType
        });
        this.affectedUserCountRequest = request;
        request.$promise.then((preview) => {
            // ignore answers to requests the user has already typed past
            if (this.affectedUserCountRequest === request) {
                this.affectedUserCount = preview.userCount;
            }
        }).catch(() => {
            if (this.affectedUserCountRequest === request) {
                this.affectedUserCount = null;
            }
        });
    }

    confirmAndSave () {
        const model = this.form.model;
        if (!model.isInactiveUserAnonymizationEnabled) {
            this.save();
            return;
        }

        this.CompanyResource.inactiveUserAnonymizationPreview({
            period: model.inactiveUserAnonymizationPeriod,
            periodType: model.inactiveUserAnonymizationPeriodType
        }).$promise.then((preview) => {
            this.ModalService.confirm(this.getConfirmMessage(preview), 'medium')
                .then(() => this.save())
                .catch(() => {});
        }).catch((response) => {
            // an invalid period fails the save the same way, which shows the errors next to the fields
            if (response.status === 422) {
                this.save();
                return;
            }
            this.toastr.error(this.$translate.instant('general.Something went wrong'));
        });
    }

    getConfirmMessage (preview) {
        if (!preview.userCount) {
            return this.$translate.instant('company.inactiveUserAnonymization.confirmNone');
        }
        return this.$translate.instant('company.inactiveUserAnonymization.confirm', { count: preview.userCount });
    }

    beforeSave () {
        // a failed save leaves the codes in place, so a retry must not map them a second time
        this.form.model.languages = this.form.model.languages.map(
            language => (typeof language === 'string' ? language : language.language)
        );
        if (!this.form.model.isInactiveUserAnonymizationEnabled) {
            this.form.model.inactiveUserAnonymizationPeriod = null;
            this.form.model.inactiveUserAnonymizationPeriodType = null;
        }
    }

    save () {
        this.beforeSave();
        this.form.save().then((company) => {
            this.toastr.success(this.$translate.instant('general.Changes saved'));
            this.company = company;
            this.initForm();
        }).catch((response) => {
            this.toastr.error(this.$translate.instant('general.Something went wrong'));
            this.form.invalid = true;
            this.form.saved = false;
            this.form.errors = this.parseErrors(response);
        });
    }

    parseErrors (response) {
        return response.data.reduce((original, error) => {
            if (error.path) {
                this.lodash.set(original, `${error.path}.${error.field}`, error.message);
            }
            else {
                original[error.field] = error.message;
            }
            return original;
        }, {});
    }
}



// WEBPACK FOOTER //
// ./src/app/components/manage/settings/privacy/index.js